import type {
  RpAlign,
  RpBg,
  RpCols,
  RpOverlay,
  RpPad,
  RpRadius,
  RpTone,
  RpWidth,
} from '@mlm/shared';

/**
 * Replicated-page style tokens → Tailwind class strings.
 *
 * WHY THIS FILE LIVES IN @mlm/ui, not @mlm/shared: both `apps/member` and
 * `apps/admin` glob `../../packages/ui/src/**` in their `tailwind.config.cjs`,
 * and NEITHER globs `packages/shared`. A class string written in shared is
 * silently purged from both bundles — it typechecks, ships, and renders
 * unstyled. Keep every class literal here.
 *
 * Every value below is a COMPLETE literal. Never build a class by interpolation
 * (`bg-${x}`): Tailwind's scanner is a regex over source text, so an
 * interpolated class is never emitted. `static-classes.test.ts` guards this.
 *
 * Every lookup CLAMPS: a stored token outside its union falls back to the
 * baseline rather than yielding `undefined`. These pages are public and can be
 * older than any given deploy.
 */

export const RP_BG: Record<RpBg, string> = {
  page: 'bg-background text-foreground',
  muted: 'bg-muted text-foreground',
  tint: 'bg-accent text-accent-foreground',
  accent: 'bg-primary text-primary-foreground',
  dark: 'bg-neutral-900 text-neutral-50',
  // The picture itself is an inline `backgroundImage` (a URL cannot be a class);
  // this only sets the default text color that sits over the scrim.
  image: 'text-white',
};

export const RP_WIDTH: Record<RpWidth, string> = {
  narrow: 'mx-auto w-full max-w-2xl px-4',
  normal: 'mx-auto w-full max-w-4xl px-4',
  wide: 'mx-auto w-full max-w-6xl px-4 sm:px-6',
  // The one that breaks the historic max-w-2xl column and goes edge-to-edge.
  full: 'w-full px-4 sm:px-6',
};

export const RP_PAD: Record<RpPad, string> = {
  none: 'py-0',
  xs: 'py-3',
  sm: 'py-6',
  md: 'py-10',
  lg: 'py-16',
  xl: 'py-24',
};

export const RP_ALIGN: Record<RpAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/** Flex main-axis alignment matching a text alignment (for button rows). */
export const RP_JUSTIFY: Record<RpAlign, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

/** Margin helper that centers a constrained child inside an aligned band. */
export const RP_MX: Record<RpAlign, string> = {
  left: 'mr-auto',
  center: 'mx-auto',
  right: 'ml-auto',
};

export const RP_COLS: Record<RpCols, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
};

export const RP_OVERLAY: Record<RpOverlay, string> = {
  0: 'bg-black/0',
  20: 'bg-black/20',
  40: 'bg-black/40',
  60: 'bg-black/60',
  80: 'bg-black/80',
};

export const RP_RADIUS: Record<RpRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded',
  md: 'rounded-lg',
  lg: 'rounded-xl',
  xl: 'rounded-3xl',
};

/** CSS value for `--radius`, for the page-theme override. */
export const RP_RADIUS_CSS: Record<RpRadius, string> = {
  none: '0px',
  sm: '0.25rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1.5rem',
};

export const RP_TONE: Record<RpTone, string> = {
  auto: '',
  dark: 'text-neutral-900',
  light: 'text-white',
};

export const RP_HERO_MIN_H: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'min-h-[320px]',
  md: 'min-h-[460px]',
  lg: 'min-h-[640px]',
};

export const RP_SPACER_H: Record<'sm' | 'md' | 'lg' | 'xl', string> = {
  sm: 'h-4',
  md: 'h-10',
  lg: 'h-20',
  xl: 'h-32',
};

export const RP_IMAGE_MAX_W: Record<'sm' | 'md' | 'lg' | 'full', string> = {
  sm: 'max-w-xs',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  full: 'max-w-full',
};

export const RP_ASPECT: Record<'16:9' | '9:16' | '4:3', string> = {
  '16:9': 'aspect-video',
  '9:16': 'aspect-[9/16]',
  '4:3': 'aspect-[4/3]',
};

// ── Buttons ─────────────────────────────────────────────────────────────────

// Font weight lives on the VARIANT, not here: a solid button carries more weight
// than an outlined one, and it keeps a converted legacy button (which was
// `font-medium`) visually unchanged.
export const RP_BTN_BASE =
  'inline-flex items-center justify-center rounded-md transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export const RP_BTN_VARIANT: Record<'primary' | 'outline' | 'ghost' | 'light', string> = {
  primary: 'bg-primary font-semibold text-primary-foreground',
  // Mirrors the pre-builder `button` block's look, so a converted legacy button
  // renders unchanged. Don't restyle without re-reviewing the legacy snapshot.
  outline: 'border border-primary font-medium text-primary',
  ghost: 'font-medium text-primary underline underline-offset-4',
  light: 'bg-white font-semibold text-neutral-900',
};

export const RP_BTN_SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-2.5 text-sm',
  lg: 'px-8 py-3 text-base',
};

// ── Clamped lookups ─────────────────────────────────────────────────────────

export const rpBg = (v?: RpBg) => RP_BG[v ?? 'page'] ?? RP_BG.page;
export const rpWidth = (v?: RpWidth) => RP_WIDTH[v ?? 'normal'] ?? RP_WIDTH.normal;
export const rpPad = (v?: RpPad) => RP_PAD[v ?? 'md'] ?? RP_PAD.md;
export const rpAlign = (v?: RpAlign) => RP_ALIGN[v ?? 'left'] ?? RP_ALIGN.left;
export const rpJustify = (v?: RpAlign) => RP_JUSTIFY[v ?? 'left'] ?? RP_JUSTIFY.left;
export const rpMx = (v?: RpAlign) => RP_MX[v ?? 'left'] ?? RP_MX.left;
export const rpCols = (v?: RpCols) => RP_COLS[v ?? 3] ?? RP_COLS[3];
export const rpOverlay = (v?: RpOverlay) => RP_OVERLAY[v ?? 40] ?? RP_OVERLAY[40];
export const rpRadius = (v?: RpRadius) => RP_RADIUS[v ?? 'md'] ?? RP_RADIUS.md;
export const rpTone = (v?: RpTone) => RP_TONE[v ?? 'auto'] ?? RP_TONE.auto;
